import { useState, useEffect, useCallback, useRef } from 'react';
import { setProductReaction, getImageUrl } from '../utils/api';
import CampaignBadge from './CampaignBadge';
import CampaignCountdown from './CampaignCountdown';

// Countdown helper for simple display
const getTimeRemaining = (endTime) => {
    if (!endTime) return null;
    const total = endTime - new Date();
    if (total <= 0) return null;
    
    const hours = Math.floor(total / (1000 * 60 * 60));
    const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) return null;
    if (hours > 0) return `${hours} saat ${minutes} dk kaldı`;
    return `${minutes} dk kaldı`;
};

// Detailed countdown component removed - using imported component

// Dietary tag icon definitions with colors
const DIETARY_ICONS = {
    is_vegan: { icon: '🌱', label: 'Vegan', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    is_vegetarian: { icon: '🥬', label: 'Vejetaryen', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    is_gluten_free: { icon: '🌾', label: 'Glutensiz', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    contains_gluten: { icon: '⚠️', label: 'Gluten', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    is_spicy: { icon: '🌶️', label: 'Acı', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    is_sweet: { icon: '🍯', label: 'Tatlı', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
    is_new: { icon: '✨', label: 'Yeni', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    is_popular: { icon: '🔥', label: 'Popüler', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    is_chef_recommended: { icon: '👨‍🍳', label: 'Şef Önerisi', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' }
};

// Allergen definitions
const ALLERGEN_LABELS = {
    gluten: 'Gluten',
    dairy: 'Süt',
    eggs: 'Yumurta',
    nuts: 'Kuruyemiş',
    peanuts: 'Fıstık',
    soy: 'Soya',
    fish: 'Balık',
    shellfish: 'Deniz Ürünü',
    sesame: 'Susam',
    celery: 'Kereviz',
    mustard: 'Hardal',
    sulfites: 'Sülfitler'
};

const ProductDetailModal = ({ product, isOpen, onClose, onLike, settings = {}, isSettingEnabled = () => true, categoryProducts = [], onProductChange, restaurantName = '', slug = '', campaigns = [], allProducts = [] }) => {
    const [reaction, setReaction] = useState(null);
    const [likeCount, setLikeCount] = useState(0);
    const [dislikeCount, setDislikeCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const modalRef = useRef(null);
    
    // Separate state for image navigation (independent from product navigation)
    const [isImageDragging, setIsImageDragging] = useState(false);
    const [imageStartX, setImageStartX] = useState(0);
    const [imageTranslateX, setImageTranslateX] = useState(0);
    
    // Update reaction state when product changes
    useEffect(() => {
        if (product) {
            // Set reaction from product data
            if (product.reaction) {
                setReaction(product.reaction);
            } else if (product.liked) {
                // Backward compatibility
                setReaction('like');
            } else {
                setReaction(null);
            }
            setLikeCount(product.like_count || 0);
            setDislikeCount(product.dislike_count || 0);
            // Reset image index when product changes
            setCurrentImageIndex(0);
        }
    }, [product, isOpen]);

    // Calculate discount info
    const discountInfo = (() => {
        if (!product) return { displayPrice: 0, originalPrice: null, discountPercent: null, hasActiveDiscount: false, discountEndTime: null };
        
        let displayPrice = parseFloat(product.price) || 0;
        let originalPrice = null;
        let discountPercent = null;
        let hasActiveDiscount = false;
        let discountEndTime = null;
        let isCombinationCampaign = false;

        if (product.is_discount_active && product.discount_price) {
            originalPrice = displayPrice;
            displayPrice = parseFloat(product.discount_price);
            discountPercent = product.discount_percentage;
            hasActiveDiscount = true;
            
            // Calculate end time for countdown (use Turkey timezone UTC+3)
            if (product.discount_end_date || product.discount_end_time) {
                // Get today's date in Turkey timezone
                const now = new Date();
                const turkeyOffset = 3 * 60; // UTC+3 in minutes
                const localOffset = now.getTimezoneOffset();
                const turkeyTime = new Date(now.getTime() + (turkeyOffset + localOffset) * 60 * 1000);
                const todayTurkey = `${turkeyTime.getFullYear()}-${String(turkeyTime.getMonth() + 1).padStart(2, '0')}-${String(turkeyTime.getDate()).padStart(2, '0')}`;
                
                const endDate = product.discount_end_date ? product.discount_end_date.split('T')[0] : todayTurkey;
                const endTime = product.discount_end_time ? product.discount_end_time.substring(0, 5) : '23:59';
                
                // Create end time in Turkey timezone
                discountEndTime = new Date(`${endDate}T${endTime}:00+03:00`);
            }
        } else if (product.campaign_prices && product.campaign_prices.length > 0) {
            // Use the first applicable campaign price
            const campaignPrice = product.campaign_prices[0];
            
            if (campaignPrice.campaign_type === 'discount' && campaignPrice.discounted_price) {
                originalPrice = parseFloat(campaignPrice.original_price) || displayPrice;
                displayPrice = parseFloat(campaignPrice.discounted_price) || displayPrice;
                const rawDiscount = campaignPrice.discount_percentage || 
                    (campaignPrice.discount_type === 'percentage' ? campaignPrice.discount_value : null);
                discountPercent = rawDiscount ? Math.round(parseFloat(rawDiscount)) : null;
                hasActiveDiscount = true;
                
                if (campaignPrice.end_date) {
                    discountEndTime = new Date(campaignPrice.end_date);
                }
            } else if (campaignPrice.campaign_type === 'combination' && campaignPrice.combo_price) {
            // For combination campaigns, don't show product-level discount
            // Combination campaigns are shown in campaign info section, not as product discount
            isCombinationCampaign = true;
            // Keep original product price, don't modify displayPrice
            // hasActiveDiscount = false; // Don't show discount badge for combination campaigns
            if (campaignPrice.end_date) {
                discountEndTime = new Date(campaignPrice.end_date);
            }
        } else if (campaignPrice.campaign_type === 'cross_product' || campaignPrice.campaign_type === 'cross_category') {
            if (campaignPrice.discounted_price) {
                originalPrice = parseFloat(campaignPrice.original_price) || displayPrice;
                displayPrice = parseFloat(campaignPrice.discounted_price) || displayPrice;
                const rawDiscount = campaignPrice.discount_type === 'percentage' ? campaignPrice.discount_value : null;
                discountPercent = rawDiscount ? Math.round(parseFloat(rawDiscount)) : null;
                hasActiveDiscount = true;
                
                if (campaignPrice.end_date) {
                    discountEndTime = new Date(campaignPrice.end_date);
                }
            }
        }
        }
        // Check if product is part of a combination campaign (from campaigns prop)
        else if (campaigns && campaigns.length > 0 && allProducts && allProducts.length > 0) {
            // Find combination campaigns that include this product
            const combinationCampaign = campaigns.find(c => {
                if ((c.campaign_type === 'combination' || c.campaign_type === 'combo') && c.combo_price) {
                    // Check if product is in combination_rules
                    if (c.combination_rules && Array.isArray(c.combination_rules)) {
                        return c.combination_rules.some(rule => rule.product_id === product.id);
                    }
                    // Or check if product is in campaign.products
                    if (c.products && Array.isArray(c.products)) {
                        return c.products.some(p => p.id === product.id);
                    }
                }
                return false;
            });
            
            if (combinationCampaign && combinationCampaign.combo_price) {
                // For combination campaigns, don't show product-level discount
                // Combination campaigns are shown in campaign info section, not as product discount
                isCombinationCampaign = true;
                // Keep original product price, don't modify displayPrice
                // hasActiveDiscount = false; // Don't show discount badge for combination campaigns
                if (combinationCampaign.end_date) {
                    discountEndTime = new Date(combinationCampaign.end_date);
                }
            }
        }
        // Fallback to old campaign structure
        else if (product.campaigns && product.campaigns.length > 0) {
            const campaign = product.campaigns[0];
            if (campaign.campaign_type === 'discount' || campaign.type === 'discount') {
                const discountType = campaign.discount_type || campaign.discountType;
                const discountValue = campaign.discount_value || campaign.discountValue;
                if (discountType === 'percentage') {
                    const roundedDiscount = discountValue ? Math.round(parseFloat(discountValue)) : null;
                    discountPercent = roundedDiscount;
                    displayPrice = displayPrice * (1 - parseFloat(discountValue) / 100);
                } else if (discountType === 'fixed_amount') {
                    displayPrice = Math.max(0, displayPrice - discountValue);
                }
                originalPrice = parseFloat(product.price) || 0;
                hasActiveDiscount = true;
                
                if (campaign.end_date) {
                    discountEndTime = new Date(campaign.end_date);
                }
            }
        }
        
        return { displayPrice, originalPrice, discountPercent, hasActiveDiscount, discountEndTime, isCombinationCampaign };
    })();

    const { displayPrice, originalPrice, discountPercent, hasActiveDiscount, discountEndTime, isCombinationCampaign } = discountInfo;
    
    // Debug log - Always log when modal is open
    useEffect(() => {
        console.log('🔍 ProductDetailModal - useEffect triggered:', { isOpen, hasProduct: !!product, productId: product?.id });
        if (isOpen && product) {
            console.log('🔍 ProductDetailModal - Opened:', {
                productId: product.id,
                productName: product.name,
                displayPrice,
                originalPrice,
                isCombinationCampaign,
                hasActiveDiscount,
                product_campaign_prices: product.campaign_prices,
                product_campaigns: product.campaigns,
                campaigns_length: campaigns?.length || 0,
                allProducts_length: allProducts?.length || 0,
                campaigns: campaigns?.map(c => ({ id: c.id, type: c.campaign_type, combo_price: c.combo_price }))
            });
        }
    }, [isOpen, product]);

    // Update countdown timer - must be called before any early return
    useEffect(() => {
        if (!discountEndTime || !hasActiveDiscount) {
            setTimeRemaining(null);
            return;
        }
        
        const updateTimer = () => {
            setTimeRemaining(getTimeRemaining(discountEndTime));
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, 60000);
        
        return () => clearInterval(interval);
    }, [product?.discount_end_date, product?.discount_end_time, hasActiveDiscount, discountEndTime]);

    // Get current product index in category
    const getCurrentProductIndex = useCallback(() => {
        if (!product || !categoryProducts || categoryProducts.length === 0) return -1;
        return categoryProducts.findIndex(p => p.id === product.id);
    }, [product, categoryProducts]);

    const currentProductIndex = getCurrentProductIndex();
    const canNavigateProducts = categoryProducts.length > 1 && currentProductIndex >= 0;

    // Navigate to next/previous product in same category
    const navigateToProduct = useCallback((direction) => {
        if (!canNavigateProducts || !onProductChange) return;
        
        const newIndex = direction === 'next' 
            ? (currentProductIndex + 1) % categoryProducts.length
            : (currentProductIndex - 1 + categoryProducts.length) % categoryProducts.length;
        
        const newProduct = categoryProducts[newIndex];
        onProductChange(newProduct);
        setCurrentImageIndex(0); // Reset image index when changing product
        
        // Update page title for new product
        if (newProduct?.name && restaurantName) {
            document.title = `${newProduct.name} - ${restaurantName} - QR Menü`;
        }
        
        // Send Google Analytics pageview for product navigation
        if (typeof window !== 'undefined' && newProduct) {
            // Try gtag (Google Analytics 4) - use page_view event
            if (window.gtag) {
                window.gtag('event', 'page_view', {
                    page_path: `/${slug}/product/${newProduct.id}`,
                    page_title: newProduct.name ? `${newProduct.name} - ${restaurantName || ''} - QR Menü` : 'Ürün Detayı',
                    page_location: window.location.href
                });
            }
            // Try ga (Universal Analytics - legacy)
            if (window.ga) {
                window.ga('send', 'pageview', {
                    page: `/${slug}/product/${newProduct.id}`,
                    title: newProduct.name ? `${newProduct.name} - ${restaurantName || ''} - QR Menü` : 'Ürün Detayı'
                });
            }
        }
    }, [canNavigateProducts, currentProductIndex, categoryProducts, onProductChange, restaurantName, slug]);

    // Image navigation handlers (separate from product navigation)
    const handleImageDragStart = useCallback((clientX) => {
        setIsImageDragging(true);
        setImageStartX(clientX);
        setImageTranslateX(0);
    }, []);

    const handleImageDragMove = useCallback((clientX) => {
        if (!isImageDragging) return;
        const diff = clientX - imageStartX;
        const maxDrag = 100;
        setImageTranslateX(Math.max(-maxDrag, Math.min(maxDrag, diff)));
    }, [isImageDragging, imageStartX]);

    const handleImageDragEnd = useCallback(() => {
        if (!isImageDragging) {
            setIsImageDragging(false);
            setImageTranslateX(0);
            return;
        }
        
        setIsImageDragging(false);
        
        const images = product?.images || [];
        if (images.length <= 1) {
            setImageTranslateX(0);
            return;
        }
        
        const threshold = 50;
        
        if (imageTranslateX > threshold) {
            // Swipe right - previous image
            setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        } else if (imageTranslateX < -threshold) {
            // Swipe left - next image
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }
        
        setImageTranslateX(0);
    }, [isImageDragging, imageTranslateX, product]);

    // Touch/Mouse handlers for product swipe (outside image area)
    const handleDragStart = useCallback((clientX) => {
        setIsDragging(true);
        setStartX(clientX);
        setTranslateX(0);
    }, []);

    const handleDragMove = useCallback((clientX) => {
        if (!isDragging || !canNavigateProducts) return;
        const diff = clientX - startX;
        // Limit the drag distance for better UX
        const maxDrag = 100;
        setTranslateX(Math.max(-maxDrag, Math.min(maxDrag, diff)));
    }, [isDragging, startX, canNavigateProducts]);

    const handleDragEnd = useCallback(() => {
        if (!isDragging) {
            setIsDragging(false);
            setTranslateX(0);
            return;
        }
        
        setIsDragging(false);
        
        if (!canNavigateProducts) {
            setTranslateX(0);
            return;
        }
        
        const threshold = 50; // minimum swipe distance
        
        if (translateX > threshold) {
            // Swipe right - previous product
            navigateToProduct('prev');
        } else if (translateX < -threshold) {
            // Swipe left - next product
            navigateToProduct('next');
        }
        
        setTranslateX(0);
    }, [isDragging, translateX, canNavigateProducts, navigateToProduct]);

    // Touch event handlers
    const handleTouchStart = useCallback((e) => {
        handleDragStart(e.touches[0].clientX);
    }, [handleDragStart]);

    const handleTouchMove = useCallback((e) => {
        handleDragMove(e.touches[0].clientX);
    }, [handleDragMove]);

    const handleTouchEnd = useCallback(() => {
        handleDragEnd();
    }, [handleDragEnd]);

    // Mouse event handlers
    const handleMouseDown = useCallback((e) => {
        // Allow dragging on the entire modal if multiple products exist
        // But prevent on buttons and interactive elements
        if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            return;
        }
        if (canNavigateProducts) {
            e.preventDefault();
            handleDragStart(e.clientX);
        }
    }, [canNavigateProducts, handleDragStart]);

    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            handleDragMove(e.clientX);
        }
    }, [isDragging, handleDragMove]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            handleDragEnd();
        }
    }, [isDragging, handleDragEnd]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen || !canNavigateProducts) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                navigateToProduct('prev');
            } else if (e.key === 'ArrowRight') {
                navigateToProduct('next');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, canNavigateProducts, navigateToProduct]);

    // Early return AFTER all hooks
    if (!isOpen || !product) {
        console.log('🔍 ProductDetailModal - Early return:', { isOpen, hasProduct: !!product });
        return null;
    }
    
    console.log('🔍 ProductDetailModal - Rendering:', { productId: product.id, productName: product.name });

    const images = product.images || [];
    const currentImage = images[currentImageIndex];

    // Check if this is a Nox's Choice (winner) product
    const isNoxChoice = product.is_nox_choice || product.vote?.winner_product_id === product.id;
    const campaignEndDate = product.vote?.campaign_end_date;

    const handleReaction = async (type) => {
        if (loading) return;

        setLoading(true);
        try {
            const result = await setProductReaction(product.id, type);
            setReaction(result.data.reaction);
            setLikeCount(result.data.like_count);
            setDislikeCount(result.data.dislike_count);
            if (onLike) {
                onLike(product.id, result.data.reaction);
            }
        } catch (error) {
            console.error('Reaction error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check if there are any extra details to show
    const hasDietaryTags = Object.keys(DIETARY_ICONS).some(key => product[key]);
    const hasNutrition = product.calories || product.protein || product.carbs || product.fat;
    const hasAllergens = product.allergens && product.allergens.length > 0;
    const hasExtraInfo = hasDietaryTags || hasNutrition || hasAllergens || product.preparation_time;

    return (
        <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-0 md:p-4 animate-fadeIn" 
                        onClick={onClose}
        >
            <div 
                ref={modalRef}
                className={`w-full h-auto md:h-auto md:max-h-[90vh] md:max-w-lg md:rounded-3xl shadow-2xl transform animate-slideUp flex flex-col relative ${
                    isNoxChoice 
                        ? 'bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 p-[3px] md:rounded-[28px]' 
                        : 'bg-white'
                }`}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                style={{
                    transform: `translateX(${translateX}px)`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                }}
            >
                {/* Product Navigation Arrows (Absolute Centered) */}
                {canNavigateProducts && (
                    <>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateToProduct('prev');
                            }}
                            className="hidden md:flex absolute left-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center z-50 text-gray-700 hover:text-orange-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateToProduct('next');
                            }}
                            className="hidden md:flex absolute right-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center z-50 text-gray-700 hover:text-orange-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                        {/* Mobile Swipe Hint (Optional - fades out) */}
                        <div className="md:hidden absolute inset-x-0 bottom-20 flex justify-center pointer-events-none opacity-0 animate-pulse">
                            <div className="bg-black/50 text-white px-3 py-1 rounded-full text-xs">
                                Kaydırarak gezinebilirsiniz
                            </div>
                        </div>
                    </>
                )}

                <div className={`flex flex-col h-full overflow-hidden ${isNoxChoice ? 'bg-white rounded-none md:rounded-3xl' : ''}`}>
                
                {/* Product Image Area - Responsive Aspect Ratio */}
                    {images.length > 0 && (
                    <div 
                        className="relative w-full shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 aspect-square md:aspect-square overflow-hidden"
                        onTouchStart={(e) => {
                            e.stopPropagation(); // Prevent product navigation
                            handleImageDragStart(e.touches[0].clientX);
                        }}
                        onTouchMove={(e) => {
                            e.stopPropagation();
                            handleImageDragMove(e.touches[0].clientX);
                        }}
                        onTouchEnd={(e) => {
                            e.stopPropagation();
                            handleImageDragEnd();
                        }}
                        onMouseDown={(e) => {
                            // Only handle image navigation if clicking on image area (not buttons)
                            if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO' || e.target.closest('.image-container')) {
                                e.stopPropagation();
                                handleImageDragStart(e.clientX);
                            }
                        }}
                        onMouseMove={(e) => {
                            if (isImageDragging) {
                                e.stopPropagation();
                                handleImageDragMove(e.clientX);
                            }
                        }}
                        onMouseUp={(e) => {
                            if (isImageDragging) {
                                e.stopPropagation();
                                handleImageDragEnd();
                            }
                        }}
                        onMouseLeave={() => {
                            if (isImageDragging) {
                                handleImageDragEnd();
                            }
                        }}
                    >
                        <div 
                            className="image-container w-full h-full transition-transform duration-300"
                            style={{
                                transform: `translateX(${imageTranslateX}px)`
                            }}
                        >
                            {currentImage?.media_type === 'video' ? (
                                <video
                                    src={getImageUrl(currentImage.video_url || currentImage.image_url)}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    controls
                                    muted
                                />
                            ) : (
                                <img
                                    src={getImageUrl(currentImage?.image_url)}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            )}
                        </div>
                            
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                        
                        {/* Image Navigation - Desktop Hover Arrows */}
                            {images.length > 1 && (
                                <>
                                    <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
                                    }}
                                    className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white hover:scale-110 transition-all shadow-lg z-10"
                                >
                                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentImageIndex((prev) => (prev + 1) % images.length);
                                    }}
                                    className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white hover:scale-110 transition-all shadow-lg z-10"
                                >
                                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </>
                        )}
                        
                        {/* Image Navigation Dots - Below Image */}
                        {images.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                                {images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCurrentImageIndex(index);
                                        }}
                                        className={`w-2 h-2 rounded-full transition-all ${
                                            index === currentImageIndex 
                                                ? 'bg-white w-6' 
                                                : 'bg-white/50 hover:bg-white/75'
                                        }`}
                                        aria-label={`Görsel ${index + 1}`}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Close Button (Image Mode) */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white hover:scale-110 transition-all shadow-lg z-20"
                        >
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                        
                        {/* Campaign Badge (quantity discount) */}
                        {!product.is_discount_active && (
                            <>
                                {product.campaign_prices && product.campaign_prices.length > 0 && (
                                    <div className="absolute top-4 right-16 z-20">
                                        <CampaignBadge 
                                            campaign={product.campaigns?.[0]} 
                                            campaignPrice={product.campaign_prices[0]} 
                                            product={product}
                                        />
                                    </div>
                                )}
                                {!product.campaign_prices && product.campaigns && product.campaigns.length > 0 && (
                                    <div className="absolute top-4 right-16 z-20">
                                        <CampaignBadge 
                                            campaign={product.campaigns[0]} 
                                            product={product}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                                    
                        {/* Like/Dislike Buttons */}
                        {isSettingEnabled('show_likes', true) && (
                            <div className="absolute top-4 left-4 flex gap-2 z-20">
                                <button
                                    onClick={() => handleReaction('like')}
                                    disabled={loading}
                                    className="backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center transition-all shadow-lg"
                                    style={{
                                        backgroundColor: reaction === 'like' ? 'var(--like-color, #22c55e)' : 'rgba(255,255,255,0.9)',
                                        color: reaction === 'like' ? '#fff' : 'var(--like-color, #22c55e)',
                                        transform: reaction === 'like' ? 'scale(1.1)' : 'scale(1)'
                                    }}
                                >
                                    <svg className="w-5 h-5" fill={reaction === 'like' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                    </svg>
                                </button>
                                            <button
                                    onClick={() => handleReaction('dislike')}
                                    disabled={loading}
                                    className="backdrop-blur-sm rounded-full w-10 h-10 flex items-center justify-center transition-all shadow-lg"
                                    style={{
                                        backgroundColor: reaction === 'dislike' ? 'var(--dislike-color, #ef4444)' : 'rgba(255,255,255,0.9)',
                                        color: reaction === 'dislike' ? '#fff' : 'var(--dislike-color, #ef4444)',
                                        transform: reaction === 'dislike' ? 'scale(1.1)' : 'scale(1)'
                                    }}
                                >
                                    <svg className="w-5 h-5" fill={reaction === 'dislike' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                                    </svg>
                                </button>
                            </div>
                        )}
                                    </div>
                )}

                {/* Content Area - Scrollable */}
                <div className="overflow-y-auto p-5 max-h-[60vh]" style={{ backgroundColor: 'var(--modal-bg-color, #ffffff)' }}>
                    {/* Close Button (No Image Mode) */}
                    {images.length === 0 && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition z-20"
                        >
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                    {/* Nox's Choice Badge */}
                    {isNoxChoice && (
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-300 text-black px-3 py-1.5 rounded-lg text-sm font-bold shadow-md">
                                <span className="text-base">🏆</span>
                                <span>Sizin Seçiminiz</span>
                            </div>
                        </div>
                    )}

                    {/* Product Name */}
                    <h2 
                        className="text-lg md:text-2xl font-bold mb-1"
                        style={{ color: 'var(--product-title-color, #111827)', fontFamily: 'var(--font-product-title)' }}
                    >
                        {product.name}
                    </h2>

                    {/* Description */}
                    {product.description && isSettingEnabled('show_descriptions', true) && (
                        <p 
                            className="text-sm text-gray-500 mb-3 leading-relaxed"
                            style={{ color: 'var(--product-description-color, #6b7280)', fontFamily: 'var(--font-product-description)' }}
                        >
                            {product.description}
                        </p>
                    )}
                    
                    {/* Nox Choice Campaign Countdown */}
                    {isNoxChoice && campaignEndDate && (
                        <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                            <div className="flex items-center justify-between">
                                <span className="text-amber-700 text-sm font-medium">Kampanya Bitiş:</span>
                                <CampaignCountdown endDate={campaignEndDate} />
                            </div>
                                    </div>
                                )}

                    {/* Campaign Info - Minimal */}
                    {(product.campaign_prices && product.campaign_prices.length > 0) || (product.campaigns && product.campaigns.length > 0) ? (
                        <div className="mb-4 pt-3 border-t border-gray-100">
                            <div className="space-y-2">
                                {product.campaigns && product.campaigns.map((campaign) => {
                                    const priceInfo = product.campaign_prices?.find(cp => cp.campaign_id === campaign.id);
                                    let priceDisplay = null;
                                    
                                    if (campaign.campaign_type === 'combination' || campaign.campaign_type === 'combo') {
                                        const comboPrice = campaign.combo_price || priceInfo?.combo_price;
                                        const totalOriginalPrice = priceInfo?.total_original_price;
                                        
                                        if (comboPrice) {
                                            if (totalOriginalPrice && totalOriginalPrice > comboPrice) {
                                                // Show crossed out original price and combo price
                                                priceDisplay = (
                                                    <span className="flex items-center gap-2">
                                                        <span className="line-through text-gray-400 text-sm">
                                                            {parseFloat(totalOriginalPrice).toFixed(0)}₺
                                                        </span>
                                                        <span className="font-bold text-red-600">
                                                            {parseFloat(comboPrice).toFixed(0)}₺
                                                        </span>
                                                    </span>
                                                );
                                            } else {
                                                priceDisplay = `${parseFloat(comboPrice).toFixed(0)}₺`;
                                            }
                                        }
                                    } else if (campaign.campaign_type === 'quantity_discount') {
                                        // For quantity discount, find rules for this product and show prices
                                        if (campaign.quantity_rules && campaign.quantity_rules.length > 0) {
                                            // Find rules that match this product
                                            const productRules = campaign.quantity_rules.filter(rule => 
                                                rule.product_id === product.id
                                            );
                                            
                                            if (productRules.length > 0) {
                                                // Get the first/best rule for this product
                                                const rule = productRules[0];
                                                
                                                // Try to determine discount type from rule if campaign.quantity_discount_type is null
                                                const discountType = campaign.quantity_discount_type || 
                                                    (rule.unit_price ? 'unit_price' : 
                                                     rule.total_price ? 'total_price' : 
                                                     (rule.buy_quantity && rule.get_quantity) ? 'buy_x_get_y' : null);
                                                
                                                if (discountType === 'unit_price' && rule.unit_price) {
                                                    // Calculate total price: min_quantity * unit_price
                                                    const totalPrice = rule.min_quantity * parseFloat(rule.unit_price);
                                                    priceDisplay = `${parseFloat(totalPrice).toFixed(0)}₺`;
                                                } else if (discountType === 'total_price' && rule.total_price) {
                                                    priceDisplay = `${parseFloat(rule.total_price).toFixed(0)}₺`;
                                                } else if (discountType === 'buy_x_get_y' && rule.buy_quantity && rule.get_quantity) {
                                                    // For buy_x_get_y, calculate price if possible
                                                    if (rule.unit_price) {
                                                        const totalPrice = rule.buy_quantity * parseFloat(rule.unit_price);
                                                        priceDisplay = `${parseFloat(totalPrice).toFixed(0)}₺`;
                                                    } else {
                                                        priceDisplay = `${rule.buy_quantity}+${rule.get_quantity}`;
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    return (
                                        <div key={campaign.id} className="flex items-center justify-between">
                                            <div className="text-sm font-medium flex items-center gap-2 text-amber-900">
                                                <span className="text-base">🎁</span>
                                                <span className="line-clamp-1">
                                                    {campaign.title || campaign.campaign_title}
                                                </span>
                                            </div>
                                            {priceDisplay && (
                                                <span className="font-bold text-red-600 text-sm whitespace-nowrap ml-2">
                                                    = {typeof priceDisplay === 'string' ? priceDisplay : priceDisplay}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {/* Extra Details */}
                    {hasExtraInfo && (
                        <div className="space-y-4 mb-20"> {/* Add margin bottom for sticky footer space */}
                            {/* Dietary Tags */}
                            {hasDietaryTags && (
                                <div className="flex flex-wrap gap-2">
                                    {Object.keys(DIETARY_ICONS).map(key => {
                                        if (product[key]) {
                                            const tag = DIETARY_ICONS[key];
                                            return (
                                                <span 
                                                    key={key}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${tag.bg} ${tag.text} border ${tag.border} rounded-full text-xs font-medium`}
                                                >
                                                    <span className="text-sm">{tag.icon}</span>
                                                    <span>{tag.label}</span>
                                </span>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            )}

                            {/* Nutrition Info */}
                            {(hasNutrition || product.preparation_time) && (
                                <div className="flex flex-wrap gap-2">
                                    {product.calories && isSettingEnabled('show_calories', false) && (
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium border border-orange-100">
                                            <span className="text-sm">🔥</span>
                                            <span className="font-bold">{product.calories}</span>
                                            <span className="text-orange-500">kcal</span>
                                        </div>
                                    )}
                                    {/* ... other nutrition items ... */}
                                    {product.preparation_time && isSettingEnabled('show_preparation_time', true) && (
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium border border-green-100">
                                            <span className="text-sm">⏱️</span>
                                            <span className="font-bold">{product.preparation_time}</span>
                                            <span className="text-green-500">dk</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Allergens */}
                            {hasAllergens && isSettingEnabled('show_allergens', true) && (
                                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                                    <span className="text-amber-500 text-lg">⚠️</span>
                                    <div>
                                        <span className="text-xs font-semibold text-amber-700 block mb-1">Alerjen Uyarısı</span>
                                        <div className="flex flex-wrap gap-1.5">
                                            {product.allergens.map((allergen, idx) => (
                                                <span 
                                                    key={idx}
                                                    className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium"
                                                >
                                                    {ALLERGEN_LABELS[allergen] || allergen}
                            </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                            )}
                        </div>

                {/* Sticky Footer - Price & Action */}
                {isSettingEnabled('show_prices', true) && (
                    <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-30 md:rounded-b-3xl">
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col gap-1">
                                {/* Don't show discount prices for combination campaigns - show only product price */}
                                {!isCombinationCampaign && originalPrice && typeof originalPrice === 'number' && !isNaN(originalPrice) && (
                                    <span className="text-xs line-through font-medium mb-1" style={{ color: '#dc2626' }}>
                                        {originalPrice.toFixed(2)} ₺
                                    </span>
                                )}
                                {typeof displayPrice === 'number' && !isNaN(displayPrice) && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span 
                                            className="text-xl font-bold"
                                            style={{ 
                                                color: (!isCombinationCampaign && hasActiveDiscount) 
                                                    ? 'var(--discount-price-color, #dc2626)' 
                                                    : 'var(--product-price-color, #16a34a)'
                                            }}
                                        >
                                            {displayPrice.toFixed(2)} ₺
                                        </span>
                                        {!isCombinationCampaign && discountPercent && (
                                            <span 
                                                className="px-2 py-0.5 text-xs font-bold rounded-full"
                                                style={{
                                                    backgroundColor: 'var(--discount-badge-bg-color, #dc2626)',
                                                    color: 'var(--discount-badge-text-color, #ffffff)'
                                                }}
                                            >
                                                %{Math.round(discountPercent)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {/* Optional: Add to Cart button could go here if needed, currently just price display */}
                        </div>
                    </div>
                )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ProductDetailModal;

